import { createSelector, OutputSelector } from 'reselect';

import { RootModel } from '../models';
import {
  ContentItemTypes,
  LocationGroupTypes,
  LocationPanelGroupTypes,
  MapLocationTypes,
} from '../../types';
import { convertDurationValue, getLocationDistanceInfo, getLocationName } from '../../utils';

export const getLocationParentGroups = createSelector<
  RootModel,
  { [key: number]: LocationGroupTypes },
  number[],
  { items: LocationPanelGroupTypes[]; rating?: string }
>(
  (state) => state.locationGroupEntity.ids,
  (state) => state.projectModel.locationGroupsIds,
  (entityIds, ids) => {
    if (!ids || !ids.length) return { items: null };

    const items: LocationPanelGroupTypes[] = [];
    const ratingValues: number[] = [];
    let childGroupIds: number[] = [];

    ids.forEach((id) => {
      const item = entityIds[id];
      // check item is parent group
      if (item && childGroupIds.indexOf(item.id) === -1) {
        items.push({ ...item, numberOfChild: item.childGroupIds.length + item.itemIds.length });
        if (item.rating) ratingValues.push(item.rating);

        // push child group ids for check with next array
        if (item.childGroupIds && !!item.childGroupIds.length) {
          childGroupIds = [...childGroupIds, ...item.childGroupIds];
        }
      }
    });

    return {
      items: items.sort((a, b) => a.orderIndex - b.orderIndex),
      rating:
        ratingValues && !!ratingValues.length
          ? (ratingValues.reduce((a, b) => a + b) / ratingValues.length).toFixed(1)
          : null,
    };
  }
);

export const getLocationGroupItemContent = (
  childGroupIds: number[],
  itemIds: number[]
): OutputSelector<
  RootModel,
  ContentItemTypes[],
  (
    groups: { [key: number]: LocationGroupTypes },
    items: { [key: number]: MapLocationTypes },
    transport: { key: string; variable: string }
  ) => ContentItemTypes[]
> =>
  createSelector<
    RootModel,
    { [key: number]: LocationGroupTypes },
    { [key: number]: MapLocationTypes },
    { key: string; variable: string },
    ContentItemTypes[]
  >(
    (state) => state.locationGroupEntity.ids,
    (state) => state.mapLocationEntity.ids,
    (state) => state.locationModel.transport,
    (locationGroupIds, mapLocationIds, transport) => {
      const items: MapLocationTypes[] = [];
      itemIds.forEach((id) => {
        const item = mapLocationIds[id];
        if (item) items.push(item);
      });

      items.sort((a, b) => a.orderIndex - b.orderIndex);
      let contentItems: ContentItemTypes[] = [
        ...items
          .map((item) => {
            let distanceInfo: { value?: number; unit?: string };
            let subTitle: string;
            if (item.distanceMatrix[transport.variable]) {
              const distanceMatrix = item.distanceMatrix[transport.variable];
              const result = getLocationDistanceInfo(distanceMatrix);
              if (result) {
                subTitle = result.distance;
                distanceInfo = convertDurationValue(result.duration);
              }
            }

            // if (!distanceInfo) return null; // remove item not have distance info case location island
            return {
              id: item.id,
              title: getLocationName(item),
              subTitle,
              distanceInfo,
              avatar: item.mapLocationInfo ? item.mapLocationInfo.avatar : undefined,
              icon: item.mapLocationInfo ? item.mapLocationInfo.icon : undefined,
              iconBgColor: item.mapLocationInfo ? item.mapLocationInfo.iconBgColor : undefined,
            };
          })
          .filter((i) => !!i),
      ];

      const childGroups: LocationGroupTypes[] = [];
      childGroupIds.forEach((id) => {
        const item = locationGroupIds[id];
        if (item) childGroups.push(item);
      });

      childGroups.sort((a, b) => a.orderIndex - b.orderIndex);
      contentItems = [
        ...contentItems,
        ...childGroups.map((item) => ({
          id: item.id,
          code: item.code,
          alias: item.alias,
          title: item.name,
          subTitle: `${item.numberOfLocation}`,
          avatar: item.avatar,
          icon: item.avatar,
          isSubGroup: true,
        })),
      ];

      return contentItems;
    }
  );

export const getSelectedGroup = createSelector<
  RootModel,
  { [key: number]: LocationGroupTypes },
  number,
  LocationGroupTypes
>(
  (state) => state.locationGroupEntity.ids,
  (state) => state.locationModel.groupId,
  (entityIds, id) => entityIds[id]
);

export const getSelectedSubGroup = createSelector<
  RootModel,
  { [key: number]: LocationGroupTypes },
  number,
  LocationPanelGroupTypes
>(
  (state) => state.locationGroupEntity.ids,
  (state) => state.locationModel.subGroupId,
  (entityIds, id) => entityIds[id] as LocationPanelGroupTypes
);

export const getLocationGroups = createSelector<
  RootModel,
  { [key: number]: LocationGroupTypes },
  number[],
  LocationPanelGroupTypes[]
>(
  (state) => state.locationGroupEntity.ids,
  (state) => state.projectModel.locationGroupsIds,
  (entityIds, ids) => {
    if (!ids || !ids.length) return [];

    const items: LocationPanelGroupTypes[] = [];

    let childItemIds: number[] = [];
    ids.forEach((id) => {
      let item = {
        ...entityIds[id],
        numberOfChild: entityIds[id].childGroupIds.length + entityIds[id].itemIds.length,
      };
      if (!item || childItemIds.indexOf(item.id) !== -1) return;

      if (item.childGroupIds && item.childGroupIds.length) {
        childItemIds = [...childItemIds, ...item.childGroupIds];
      }

      if (
        // have locations
        (item.itemIds && item.itemIds.length) ||
        // not have locations and have child groups
        ((!item.itemIds || !item.itemIds.length) && item.childGroupIds && item.childGroupIds.length)
      ) {
        if (item.childGroupIds && item.childGroupIds.length) {
          item = {
            ...item,
            childGroups: item.childGroupIds.map((i) => entityIds[i]),
            numberOfChild: item.childGroupIds.length + item.itemIds.length,
          };
        }

        items.push(item);
      }
    });

    return items.sort((a, b) => a.orderIndex - b.orderIndex);
  }
);
